import React from 'react'
import PropTypes from 'prop-types'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import styled from 'styled-components'
import ReactPaginate from 'react-paginate'
import TooltipComponent from '../headerTooltip'
import TableControls from '../tableControls'
import { StyledPaper } from '../../../theme/Table'
import APILoadingSpinnerRenderer from '../apiLoadingSpinner'
import EmptyTableMessage from '../emptyTableMessage'
import { EMPTY_TABLE_MESSAGE } from '../../../common/enum/emptyTableMessage'
import { PaginationWrapper, PaginationArrow, FooterText } from '../../../theme/Global'

import {
  CustomArrowDown,
  CustomArrowUp,
  CustomTableHead,
  CustomTableBodyCell,
  CustomTableBodyRow,
  CustomTableHeadCell,
  CustomTableHeadRow,
  CustomTableHeadLoadingRow,
  DataTableWrapper,
  CustomH5
} from './styles'

const SortWrapper = styled.div`
  position: relative;
  display: inline-block;
  top: 7px;
  && svg {
    position: absolute;
    top: -18px;
    height: 18px !important;
    width: 18px !important;
  }
`

const fixedTableLayout = { tableLayout: 'fixed' }

const StyledCell = styled.div`
  font-size: 12px;
  padding: 1rem;
`

const DataTableRenderer = ({
  columns,
  currentPage,
  data,
  defaultLocale,
  defaultRange,
  defaultSearch,
  exports,
  onChangePagination,
  onExport,
  onFilter,
  onHeaderClick,
  perPage,
  recordCount,
  recordType,
  showCount,
  showControls,
  showDateFilter,
  showExport,
  showLocaleFilter,
  showSearch,
  sortBy,
  sortDirection,
  sortText,
  stateList,
  registerDateEvent,
  onSearchFinished,
  isLoading
}) => {
  const startRow = currentPage * perPage
  const endRow = startRow + perPage
  const totalPages = Math.ceil(recordCount / perPage)
  const isTable = true
  // Render rows
  const dataRows = []

  for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
    const row = data({ index: rowIndex })
    if (row) {
      dataRows.push(
        <CustomTableBodyRow key={row.id}>
          {columns.map((column) => {
            const cell = row[column.key]
            return (
              <CustomTableBodyCell
                key={column.key}
                numeric
                style={{ paddingLeft: (column.noPaddingLeft) ? '0px' : '' }}
              >
                <StyledCell>
                  {cell}
                </StyledCell>
              </CustomTableBodyCell>
            )
          })}
        </CustomTableBodyRow>
      )
    }
  }

  let tableBody = (
    <EmptyTableMessage
      columns={columns.length}
      text={EMPTY_TABLE_MESSAGE.NO_RESULTS}
    />
  )

  if (dataRows.length > 0) {
    tableBody = dataRows.map((row) => {
      return row
    })
  }


  const columnsTotalWidth = columns.reduce((a, c) => a + c.width, 0)

  return (
    <DataTableWrapper>
      {showControls ? (
        <TableControls
          defaultLocale={defaultLocale}
          defaultRange={defaultRange}
          defaultSearch={defaultSearch}
          exports={exports}
          onExport={onExport}
          onFilter={onFilter}
          recordType={recordType}
          recordCount={recordCount}
          showCount={showCount}
          showDateFilter={showDateFilter}
          showExport={showExport}
          showLocaleFilter={showLocaleFilter}
          showSearch={showSearch}
          showAddLocationButton={false} // @Todo
          stateList={stateList}
          registerDateEvent={registerDateEvent}
          onSearchFinished={onSearchFinished}
        />
      ) : null}
      <StyledPaper>
        <div
          style={{
            display: 'flex',
            height: '100%',
            alignSelf: 'stretch',
            flex: '1 1 auto'
          }}
        >
          <Table style={fixedTableLayout}>
            <CustomTableHead>
              { isLoading ? (<CustomTableHeadLoadingRow><th colSpan={columns.length} /></CustomTableHeadLoadingRow>) : (
                <CustomTableHeadRow>
                  {columns.map((column) => (
                    <CustomTableHeadCell
                      key={column.key}
                      onClick={onHeaderClick.bind(null, column.key)}
                      style={{ width: `${(column.width * 100) / columnsTotalWidth}%` }}
                    >
                      {column.toolTip ? (
                        typeof column.sort !== 'function' && sortBy === column.key ? (
                          <CustomH5>
                            <TooltipComponent toolTip={sortText} labelText={sortText} />
                          </CustomH5>
                        ) : (
                          <CustomH5>
                            <TooltipComponent toolTip={column.toolTip} labelText={column.text} />
                          </CustomH5>
                        )
                      ) : typeof column.sort !== 'function' && sortBy === column.key ? (
                        <CustomH5>{sortText}</CustomH5>
                      ) : (
                        <CustomH5>{column.text}</CustomH5>
                      )}
                      {sortBy === column.key ? (
                        sortDirection === 'DESC' ? (
                          <SortWrapper>
                            {CustomArrowDown}
                          </SortWrapper>
                        ) : (
                          <SortWrapper>
                            {CustomArrowUp}
                          </SortWrapper>
                        )
                      ) : null}
                    </CustomTableHeadCell>
                  ))}
                </CustomTableHeadRow>
              )}
            </CustomTableHead>
            <TableBody style={{ height: '100%' }}>
              {isLoading ? (<APILoadingSpinnerRenderer isTable={isTable} colspan={columns.length} />) : tableBody}
            </TableBody>
          </Table>
        </div>
      </StyledPaper>
      {totalPages > 1 && !isLoading ? (
        <PaginationWrapper>
          <FooterText>
            Showing {startRow + 1} to {endRow > recordCount ? recordCount : endRow} of {recordCount}{' '}
            {recordType}
          </FooterText>
          <ReactPaginate
            containerClassName="pagination"
            initialPage={currentPage}
            forcePage={currentPage}
            marginPagesDisplayed={2}
            nextLabel={(
              <span data-testid="pagination-next">
                Next{' '} <PaginationArrow direction="left" />
              </span>
            )}
            pageCount={totalPages}
            pageRangeDisplayed={5}
            onPageChange={onChangePagination}
            previousLabel={(
              <span data-testid="pagination-prev">
                <PaginationArrow direction="right" /> Prev
              </span>
            )}
          />
        </PaginationWrapper>
      ) : null}
    </DataTableWrapper>
  )
}

DataTableRenderer.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.func.isRequired,
  exports: PropTypes.arrayOf(PropTypes.string).isRequired,
  headerHeight: PropTypes.number.isRequired,
  onExport: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  noRowsRenderer: PropTypes.func.isRequired,
  recordCount: PropTypes.number.isRequired,
  recordType: PropTypes.string.isRequired,
  rowHeight: PropTypes.number.isRequired,
  showCount: PropTypes.bool.isRequired,
  showControls: PropTypes.bool.isRequired,
  showDateFilter: PropTypes.bool.isRequired,
  showExport: PropTypes.bool.isRequired,
  showLocaleFilter: PropTypes.bool.isRequired,
  showSearch: PropTypes.bool.isRequired,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string
}

DataTableRenderer.defaultProps = {
  sortBy: null,
  sortDirection: 'ASC'
}

export default DataTableRenderer
